import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigProvider, Table } from 'antd';
import type { TableColumnsType } from 'antd';
import { PLATFORM_TABLE_THEME } from './tableTheme';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { BIND_CHIP, BIND_LABEL, type Binding } from './mockData';
import { usePlatformState } from '../../../features/platform/platformStateSlice';
import {
  useGetTerritoriesQuery,
  useGetStateDepartmentsQuery,
  useGetPeopleQuery,
  type TerritoryRow,
} from '../../../features/platform/platformApi';

/* ------------------------------------------------------------------ *
 * Platform Admin — Work queues (prototype `wq`).
 * What is still missing across every department, in two tabs:
 *  - Territories to map (own levels, least mapped first)
 *  - Posts not on record (posts with no person)
 * Each item opens in its department. Wired to the Platform Admin API.
 * ------------------------------------------------------------------ */

const covColor = (c: number) => (c >= 100 ? 'text-brand' : c >= 70 ? 'text-[#8a4b0f]' : 'text-[#b42318]');
const barColor = (c: number) => (c >= 100 ? 'bg-brand' : c >= 70 ? 'bg-[#8a4b0f]' : 'bg-[#b42318]');

const sourceText = (bind: Binding) =>
  bind === 'grouped'
    ? 'made of groups of units'
    : bind === 'booth'
      ? 'made of booths'
      : bind === 'village'
        ? 'made of villages'
        : 'listed one by one';

/** The posts-not-on-record rows for one department (its people ladder, filtered). */
function DeptPostsNotOnRecord({
  stateCode,
  templateCode,
  deptName,
  onCount,
}: {
  stateCode: string;
  templateCode: string;
  deptName: string;
  onCount: (templateCode: string, n: number) => void;
}) {
  const navigate = useNavigate();
  const { data: ladder = [] } = useGetPeopleQuery({ code: stateCode, template_code: templateCode });
  const missing = ladder.filter((r) => r.recorded < r.units);

  useEffect(() => {
    onCount(templateCode, missing.length);
  }, [templateCode, missing.length, onCount]);

  return (
    <>
      {missing.map((p) => (
        <div
          key={`${templateCode}:${p.level}:${p.designation}`}
          className="flex flex-wrap items-center justify-between gap-2.5 border-b border-[#f1f5f9] px-4 py-2.5 last:border-b-0"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-ink">{p.designation}</span>
              <span className={['rounded px-1.5 py-0.5 text-[11px] font-semibold', p.type === 'elected' ? 'bg-[#ede7f6] text-[#4a2c82]' : 'bg-brand-shade text-brand'].join(' ')}>
                {p.type === 'elected' ? 'Elected' : 'Official'}
              </span>
            </div>
            <div className="text-xs text-ink-secondary">
              {deptName} · {p.level} · {p.recorded} of {p.units} recorded
            </div>
          </div>
          <button
            type="button"
            onClick={() =>
              navigate(
                `/admin/departments/${templateCode}?tab=People&level=${encodeURIComponent(p.level)}&designation=${encodeURIComponent(p.designation)}`,
              )
            }
            className="h-9 shrink-0 rounded-lg border border-brand bg-white px-3 text-xs font-semibold text-brand transition-colors hover:bg-brand-shade"
          >
            Record person
          </button>
        </div>
      ))}
    </>
  );
}

export default function WorkQueuesPage() {
  const navigate = useNavigate();
  const { code: stateCode, name: stateName } = usePlatformState();
  const [tab, setTab] = useState<'terr' | 'posts'>('terr');
  const [postCounts, setPostCounts] = useState<Record<string, number>>({});

  const { data: territories = [], isLoading: terrLoading } = useGetTerritoriesQuery(stateCode, { skip: !stateCode });
  const { data: depts = [] } = useGetStateDepartmentsQuery(stateCode, { skip: !stateCode });

  const onCount = (templateCode: string, n: number) =>
    setPostCounts((c) => (c[templateCode] === n ? c : { ...c, [templateCode]: n }));
  const totalMissing = Object.values(postCounts).reduce((a, b) => a + b, 0);

  const tabBtn = (key: 'terr' | 'posts', label: string) => (
    <button
      key={key}
      type="button"
      aria-pressed={tab === key}
      onClick={() => setTab(key)}
      className={[
        'h-10 rounded-lg px-3.5 text-[13px] font-semibold transition-colors',
        tab === key ? 'bg-brand text-white' : 'border border-line bg-white text-ink-secondary hover:bg-brand-shade',
      ].join(' ')}
    >
      {label}
    </button>
  );

  const columns: TableColumnsType<TerritoryRow> = [
    { title: 'Department', key: 'dept', render: (_, r) => <span className="text-[13px] text-ink">{r.dept}</span> },
    { title: 'Level', key: 'level', width: 160, render: (_, r) => <span className="font-semibold text-ink">{r.level}</span> },
    {
      title: 'Binding',
      key: 'bind',
      width: 140,
      render: (_, r) => (
        <span className={['inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold', BIND_CHIP[r.binding]].join(' ')}>
          {r.binding_label || BIND_LABEL[r.binding]}
        </span>
      ),
    },
    { title: 'Units are', key: 'src', width: 200, render: (_, r) => <span className="text-xs text-[#434655]">{sourceText(r.binding)}</span> },
    { title: 'Units', key: 'units', width: 80, render: (_, r) => <span className="text-[13px] text-ink">{r.units}</span> },
    {
      title: 'Coverage',
      key: 'cov',
      width: 160,
      render: (_, r) => (
        <div className="flex flex-col gap-1">
          <span className="block h-1.5 overflow-hidden rounded-full bg-muted-bg">
            <span className={['block h-1.5 rounded-full', r.coverage == null ? '' : barColor(r.coverage)].join(' ')} style={{ width: r.coverage == null ? '0%' : `${r.coverage}%` }} />
          </span>
          <span className={['text-xs', r.coverage == null ? 'text-ink-secondary' : covColor(r.coverage)].join(' ')}>
            {r.coverage == null ? 'not counted' : `${r.coverage}%`}
          </span>
        </div>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 170,
      align: 'right',
      render: (_, r) => (
        <button
          type="button"
          onClick={() => navigate(`/admin/departments/${r.template_code}/units/${encodeURIComponent(r.level)}`)}
          className="h-9 whitespace-nowrap rounded-lg bg-[#73300b] px-3 text-xs font-semibold text-white transition-colors hover:bg-[#5a2609]"
        >
          Open units editor
        </button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-[18px]">
      <Breadcrumbs items={[{ label: 'All states', to: '/admin/overview' }, { label: 'Work queues' }]} />
      <section className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold text-brand">{stateName.toUpperCase()} · ACROSS DEPARTMENTS</span>
        <h1 className="text-2xl font-semibold leading-9 tracking-[-0.75px] text-ink">Work queues</h1>
        <p className="max-w-[760px] text-sm leading-6 text-ink-secondary">
          What is still missing in {stateName}, across every department. Each item opens in its
          department.
        </p>
        <div className="flex flex-wrap items-center gap-2 pt-1.5">
          {tabBtn('terr', 'Territories to map')}
          {tabBtn('posts', `Posts not on record${totalMissing ? ` (${totalMissing})` : ''}`)}
        </div>
      </section>

      {tab === 'terr' && (
        <div className="overflow-hidden rounded-xl border border-line/30 bg-white shadow-card">
          {terrLoading ? (
            <p className="p-4 text-sm text-ink-secondary">Loading territories…</p>
          ) : (
            <ConfigProvider theme={PLATFORM_TABLE_THEME}>
              <Table<TerritoryRow>
                className="chunaav-table"
                columns={columns}
                dataSource={territories}
                rowKey={(r) => `${r.template_code}:${r.level}`}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                scroll={{ x: 'max-content' }}
              />
            </ConfigProvider>
          )}
        </div>
      )}

      {/* Posts not on record: one people-ladder query per enabled department, flattened.
          Rendered always (hidden when not active) so the tab's count stays live. */}
      <section className={['overflow-hidden rounded-xl border border-line/30 bg-white shadow-card', tab === 'posts' ? '' : 'hidden'].join(' ')}>
        {depts.map((d) => (
          <DeptPostsNotOnRecord
            key={d.code}
            stateCode={stateCode}
            templateCode={d.code}
            deptName={d.name}
            onCount={onCount}
          />
        ))}
        {tab === 'posts' && totalMissing === 0 && (
          <div className="p-4 text-sm text-ink-secondary">Every post has a person on record.</div>
        )}
      </section>
    </div>
  );
}
